import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./StatusCimPage.css";
import { FILTER_ALL, toBRDate } from "../core/cimInterdicoes.utils";
import { getStatusCim } from "./statusCim.api";
import { applyPrazoToStatusCimRows, getStatusCimNowTs } from "./statusCim.timer.client";
import { useStatusCimRealtime } from "./useStatusCimRealtime";

function formatDateTimeInline(dateValue, timeValue, emptyLabel = "-") {
  if (!dateValue) return emptyLabel;
  const dateLabel = toBRDate(dateValue);
  const timeLabel = String(timeValue || "").trim();
  return timeLabel ?`${dateLabel} | ${timeLabel}` : dateLabel;
}

function FilterDropdown({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      (Array.isArray(options) ?options : [])
        .map((option) =>
          typeof option === "object"
            ? {
                value: String(option?.value ?? option?.label ?? "").trim(),
                label: String(option?.label ?? option?.value ?? "").trim(),
              }
            : {
                value: String(option ?? "").trim(),
                label: String(option ?? "").trim(),
              }
        )
        .filter((option) => option.value),
    [options]
  );

  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === String(value ?? "")) || null,
    [normalizedOptions, value]
  );

  const closeMenu = useCallback(() => {
    const activeEl = typeof document !== "undefined" ?document.activeElement : null;
    if (activeEl && rootRef.current?.contains(activeEl) && typeof activeEl.blur === "function") {
      activeEl.blur();
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closeMenu();
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [closeMenu, open]);

  useEffect(() => {
    if (!open) return undefined;

    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const rect = rootEl.getBoundingClientRect();
      const menuHeight = Math.min(Math.max(normalizedOptions.length * 38, 92), 220);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);

    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [normalizedOptions.length, open]);

  return (
    <label className="scim-field">
      <span>{label}</span>
      <div
        ref={rootRef}
        className={`scim-selectRoot ${open ?"is-open" : ""} ${openUp ?"is-up" : ""}`.trim()}
      >
        <button
          type="button"
          className="scim-selectTrigger"
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`scim-selectValue ${selectedOption ?"" : "scim-selectPlaceholder"}`.trim()}>
            {selectedOption?.label || placeholder}
          </span>
        </button>

        <div className={`scim-selectMenu ${openUp ?"scim-selectMenuUp" : ""}`.trim()} aria-hidden={!open}>
          {normalizedOptions.map((option) => {
            const selected = selectedOption?.value === option.value;
            return (
              <button
                type="button"
                key={option.value}
                className={`scim-selectOption ${selected ?"is-selected" : ""}`.trim()}
                onClick={() => {
                  onChange(option.value);
                  closeMenu();
                }}
                aria-selected={selected}
              >
                {option.label}
              </button>
            );
          })}

          {!normalizedOptions.length ?(
            <div className="scim-selectEmpty">Nenhuma opção encontrada.</div>
          ) : null}
        </div>
      </div>
    </label>
  );
}

function getPrazoClassName(item) {
  return [item?.prazoEtaCls, item?.prazoBlinkCls].filter(Boolean).join(" ");
}

function compareRows(left, right) {
  const leftPrazo = Number.isFinite(left?.prazoSortValue)
    ?left.prazoSortValue
    : Number.MAX_SAFE_INTEGER;
  const rightPrazo = Number.isFinite(right?.prazoSortValue)
    ?right.prazoSortValue
    : Number.MAX_SAFE_INTEGER;

  if (leftPrazo !== rightPrazo) return leftPrazo - rightPrazo;

  const dateTimeLeft = new Date(
    `${left.dataInicio || "1970-01-01"}T${left.horaInicio || "00:00"}:00`
  ).getTime();
  const dateTimeRight = new Date(
    `${right.dataInicio || "1970-01-01"}T${right.horaInicio || "00:00"}:00`
  ).getTime();

  if (dateTimeLeft !== dateTimeRight) return dateTimeLeft - dateTimeRight;

  return (left.id || 0) - (right.id || 0);
}

function extractRealtimeRow(payload) {
  if (payload?.row && typeof payload.row === "object") return payload.row;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return null;
}

function getRowIdentity(row = {}) {
  return {
    id: Number(row?.id || 0),
    codigo: String(row?.codigoUnico ?? row?.codigo_unico ?? "").trim(),
  };
}

function isSameRow(left, right) {
  const leftIdentity = getRowIdentity(left);
  const rightIdentity = getRowIdentity(right);

  if (leftIdentity.id > 0 && rightIdentity.id > 0) {
    return leftIdentity.id === rightIdentity.id;
  }

  return Boolean(leftIdentity.codigo && rightIdentity.codigo && leftIdentity.codigo === rightIdentity.codigo);
}

export default function StatusCimPage({ onBack }) {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtroMesa, setFiltroMesa] = useState(FILTER_ALL);
  const [filtroTipo, setFiltroTipo] = useState(FILTER_ALL);
  const [nowTs, setNowTs] = useState(() => getStatusCimNowTs());
  const abortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const lastRealtimeKeyRef = useRef({ key: "", at: 0 });

  useEffect(() => {
    const timerId = setInterval(() => {
      setNowTs(getStatusCimNowTs());
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  const loadData = useCallback(async ({ signal, silent = false } = {}) => {
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;

    if (!silent) {
      setLoading(true);
      setError("");
    }

    try {
      const data = await getStatusCim({ signal });
      if (signal?.aborted || seq !== fetchSeqRef.current) return;
      setRawRows(Array.isArray(data?.rows) ?data.rows : []);
      setError("");
    } catch (err) {
      if (signal?.aborted || seq !== fetchSeqRef.current) return;
      if (!silent) {
        setError(err?.response?.data?.error || err?.message || "Não foi possível carregar o Status CIM.");
      }
    } finally {
      if (!signal?.aborted && seq === fetchSeqRef.current && !silent) setLoading(false);
    }
  }, []);

  const refreshData = useCallback(
    ({ silent = true } = {}) => {
      abortRef.current?.abort?.();
      const controller = new AbortController();
      abortRef.current = controller;
      loadData({ signal: controller.signal, silent });
    },
    [loadData]
  );

  useEffect(() => {
    refreshData({ silent: false });
    return () => {
      abortRef.current?.abort?.();
    };
  }, [refreshData]);

  const upsertRawRow = useCallback((incoming) => {
    if (!incoming || typeof incoming !== "object") return;
    setLoading(false);
    setError("");

    setRawRows((prev) => {
      const nextRow = {
        ...incoming,
        codigoUnico: incoming?.codigoUnico ?? incoming?.codigo_unico ?? "",
      };
      const list = Array.isArray(prev) ?prev : [];
      const index = list.findIndex((item) => isSameRow(item, nextRow));

      if (index >= 0) {
        const next = list.slice();
        next[index] = {
          ...next[index],
          ...nextRow,
          mesaCco: nextRow?.mesaCco || next[index]?.mesaCco || "",
        };
        return next;
      }

      return [nextRow, ...list];
    });
  }, []);

  const removeRawRow = useCallback((payload) => {
    const row = extractRealtimeRow(payload);
    const incoming = row || payload;
    setLoading(false);
    setError("");

    setRawRows((prev) => {
      const list = Array.isArray(prev) ?prev : [];
      return list.filter((item) => !isSameRow(item, incoming));
    });
  }, []);

  const isDuplicateRealtime = useCallback((payload) => {
    const row = extractRealtimeRow(payload);
    const key = [
      payload?.ts || "",
      payload?.action || "",
      payload?.id ?? row?.id ?? "",
      payload?.codigo_unico ?? row?.codigoUnico ?? row?.codigo_unico ?? "",
    ].join("|");

    if (!key.replace(/\|/g, "")) return false;

    const now = Date.now();
    if (
      lastRealtimeKeyRef.current.key === key &&
      now - lastRealtimeKeyRef.current.at < 1200
    ) {
      return true;
    }

    lastRealtimeKeyRef.current = { key, at: now };
    return false;
  }, []);

  const handleRealtimeInvalidate = useCallback(
    (payload) => {
      if (payload?.reason === "reconnect") {
        refreshData({ silent: true });
        return;
      }

      if (isDuplicateRealtime(payload)) return;

      const action = String(payload?.action || "").trim().toUpperCase();
      const row = extractRealtimeRow(payload);

      if (action === "DELETE") {
        removeRawRow(payload);
        return;
      }

      if (row) {
        upsertRawRow(row);
        return;
      }

      refreshData({ silent: true });
    },
    [isDuplicateRealtime, refreshData, removeRawRow, upsertRawRow]
  );

  useStatusCimRealtime({
    onInvalidate: handleRealtimeInvalidate,
  });

  const rows = useMemo(() => applyPrazoToStatusCimRows(rawRows, nowTs), [nowTs, rawRows]);

  const mesaOptions = useMemo(() => {
    const values = Array.from(
      new Set(rawRows.map((item) => String(item?.mesaCco || "").trim()).filter(Boolean))
    ).sort((left, right) => left.localeCompare(right, "pt-BR", { sensitivity: "base" }));

    return [
      { value: FILTER_ALL, label: "Todas as mesas" },
      ...values.map((value) => ({ value, label: value })),
    ];
  }, [rawRows]);

  const tipoOptions = useMemo(() => {
    const values = Array.from(
      new Set(rawRows.map((item) => String(item?.tipoInterdicao || "").trim()).filter(Boolean))
    ).sort((left, right) => left.localeCompare(right, "pt-BR", { sensitivity: "base" }));

    return [
      { value: FILTER_ALL, label: "Todos os tipos" },
      ...values.map((value) => ({ value, label: value })),
    ];
  }, [rawRows]);

  const visibleRows = useMemo(() => {
    return rows
      .filter((item) => (filtroMesa === FILTER_ALL ?true : item.mesaCco === filtroMesa))
      .filter((item) => (filtroTipo === FILTER_ALL ?true : item.tipoInterdicao === filtroTipo))
      .filter((item) => item.status !== "Finalizado")
      .sort(compareRows);
  }, [filtroMesa, filtroTipo, rows]);

  return (
    <div className="scim-page">
      <header className="scim-appbar">
        <div className="scim-appbarWrap">
          <button type="button" className="scim-btn scim-btn--menu" onClick={onBack}>
            Menu Principal
          </button>

          <div className="scim-titleWrap">
            <h1 className="scim-title">ACOMPANHAMENTO DAS INTERDIÇÕES</h1>
          </div>

          <div className="scim-controls">
            <FilterDropdown
              label="Mesa CCO"
              value={filtroMesa}
              onChange={setFiltroMesa}
              options={mesaOptions}
              placeholder="Todas as mesas"
            />

            <FilterDropdown
              label="Tipo"
              value={filtroTipo}
              onChange={setFiltroTipo}
              options={tipoOptions}
              placeholder="Todos os tipos"
            />
          </div>
        </div>
      </header>

      <main className="scim-content">
        <section className="scim-board">
          <div className="scim-head scim-grid">
            <div>Código</div>
            <div>Início</div>
            <div>Prev. Enc.</div>
            <div>SB</div>
            <div>SUB</div>
            <div>KM</div>
            <div>Motivo</div>
            <div>Observação</div>
            <div>Mesa CCO</div>
            <div>Status</div>
          </div>

          <div className="scim-rows">
            {loading ?<div className="scim-state">Carregando interdições...</div> : null}
            {!loading && error ?<div className="scim-state scim-state--error">{error}</div> : null}
            {!loading && !error && !visibleRows.length ?(
              <div className="scim-state">
                Nenhuma interdição encontrada para os filtros selecionados.
              </div>
            ) : null}

            {!loading &&
              !error &&
              visibleRows.map((item) => (
                <article
                  key={`${item.id}-${item.codigoUnico}`}
                  className={`scim-row scim-grid ${getPrazoClassName(item)}`}
                >
                  <div className="scim-cell scim-cell--code">{item.codigoUnico || "-"}</div>
                  <div className="scim-cell">{formatDateTimeInline(item.dataInicio, item.horaInicio)}</div>
                  <div className="scim-cell">
                    {item.temPrevisao
                      ?formatDateTimeInline(item.dataFim, item.horaFim, "Sem previsão")
                      : "Sem previsão"}
                  </div>
                  <div className="scim-cell">{item.secaodebloqueio || item.sb || "-"}</div>
                  <div className="scim-cell">{item.sub || "-"}</div>
                  <div className="scim-cell">{item.km || item.kmInicial || "-"}</div>
                  <div className="scim-cell scim-cell--text">{item.motivo || "-"}</div>
                  <div className="scim-cell scim-cell--text">
                    {item.observacao || "Sem observação"}
                  </div>
                  <div className="scim-cell">{item.mesaCco || "-"}</div>
                  <div className="scim-cell scim-cell--status">{item.status || "-"}</div>
                </article>
              ))}
          </div>
        </section>
      </main>
    </div>
  );
}
